import { useCallback, useRef, useState } from 'react';

/**
 * Whether an element is actually scrolling sideways right now.
 *
 * For the "swipe to see the rest" hints. Those used to be hung on a breakpoint --
 * shown below `sm`, hidden above it -- which is a guess at when scrolling starts, and it
 * was wrong in both directions. The therapist's week grid needs 988px, so at 768px it
 * scrolled with the hint already hidden; a five-therapist chart needs 1416px and so
 * scrolls silently on a 1200px desktop, a case no breakpoint value could have covered
 * because the width that matters is the content's, not the screen's.
 *
 * Measuring removes the guess: `scrollWidth > clientWidth` is the same question the
 * scrollbar asks.
 *
 * Both the viewport and its content are observed, because either can move the answer
 * without touching the other. Narrowing the window shrinks the viewport around a fixed
 * track; switching a therapist off in the legend shrinks the track inside a fixed
 * viewport. Watching only one would miss half the transitions.
 *
 * Returns a **callback ref**, not an object ref, and that is the load-bearing detail: the
 * elements this watches are conditionally rendered -- the week grid only exists while the
 * schedule is in week view. An object ref paired with a mount effect reads `null` on the
 * render where the element does not exist yet and never looks again, so the grid scrolls
 * with no hint. A callback ref is called by React on every attach and detach, so the
 * observer follows the element in and out of the tree.
 *
 *   const [viewportRef, isScrollable] = useHorizontalOverflow<HTMLDivElement>();
 */
export const useHorizontalOverflow = <T extends HTMLElement>(): [(node: T | null) => void, boolean] => {
    const [overflows, setOverflows] = useState<boolean>(false);
    const observer = useRef<ResizeObserver | null>(null);

    // Stable identity, so React calls this only when the element genuinely attaches or
    // detaches rather than on every render.
    const measuredRef = useCallback((node: T | null): void => {
        observer.current?.disconnect();
        observer.current = null;

        if (!node) {
            setOverflows(false);
            return;
        }

        // A sub-pixel layout can leave scrollWidth a hair over clientWidth on an element
        // that is not really scrolling, and a hint that appears when there is nothing to
        // swipe to is worse than no hint at all.
        const measure = (): void => {
            setOverflows(node.scrollWidth - node.clientWidth > 1);
        };

        measure();

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(node);

        const track = node.firstElementChild;

        if (track) {
            resizeObserver.observe(track);
        }

        observer.current = resizeObserver;
    }, []);

    return [measuredRef, overflows];
};
